import { Settings } from '@mui/icons-material';
import { TabPanel } from '@mui/lab';
import { Box, CircularProgress, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Protect, Scrollbar } from 'app/components';
import { TransportAccStatusIcon } from 'app/icons';
import {
  MapTransportConditionFilterFullList,
  MapTransportConditionFilterList,
  MapTransportConditionIconsList,
  MapTransportConditionList,
  MapTransportConditionTerminalList,
  MapTransportConditionTimer,
  MapTransportConditionTrackList,
} from 'app/pages/Map';
import RenderForbidden from 'app/pages/Map/MapTransportWindow/TabPanelCondition/renderForbidden';
import {
  canStyle,
  conditionOptionsStyle,
  gridColumnsStyle,
  lastRowStyle,
  mapTransportConditionIconsListStyle,
  scrollItemStyle,
  signalStrengthStyle,
  transportConditionItemsStyle,
  transportConditionScrollItemStyle,
  transportSelectedItemStyle,
  voltageStyle,
} from 'app/pages/Map/MapTransportWindow/TabPanelCondition/TabPanelConditionStyle';
import TanksBlock from 'app/pages/Map/MapTransportWindow/TabPanelCondition/TankBlock';
import React, { forwardRef, useState } from 'react';

const CopyToClipboard = forwardRef((props, ref) => {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    navigator.clipboard.writeText(props.text);
    setCopied(true);
  };

  const handleMouseLeave = () => {
    setTimeout(() => setCopied(false), 300);
  };

  return (
    <Tooltip
      ref={ref}
      arrow
      title={copied ? 'Скопировано' : 'Скопировать в буфер'}
      placement='top'
      disableInteractive
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}>
      <Typography variant='body1'>{props.text}</Typography>
    </Tooltip>
  );
});

const TabPanelCondition = ({
  transportConditionItems,
  isFetchConditionLoading,
  handleConditionOptionsOpenClick,
  transportSelectedItem,
  transportSelectedDate,
  guardPermission,
  sensorPermission,
  relayPermission,
  fuelLevelPermission,
  handleConditionGuardOutClick,
  transportConditionSelectedItem,
  declOfNum,
  datetimeInterval,
  transportConditionListRef,
}) => {
  const transportConditionItemsReceiveDate = transportConditionItems.map((item) => ({
    time: item.receive_date ? new Date(item.receive_date).getTime() / 1000 : 0,
    receive_date: item.receive_date,
  }));
  transportConditionItemsReceiveDate.sort((a, b) => b.time - a.time);
  const lastMessageTime = transportConditionItemsReceiveDate[0]?.receive_date;
  const galileosky_device_type = ['galileosky'].includes(config.transportDeviceTypes[transportSelectedItem.device_type]);
  return (
    <TabPanel value='condition'>
      <Protect
        permissions='read'
        renderPermitted={() => (
          <React.Fragment>
            {!isFetchConditionLoading && transportConditionItems.length !== 0 && (
              <Stack
                direction='column'
                sx={{
                  height: '100%',
                  position: 'relative',
                }}>
                {transportSelectedItem.conditionOptions.open && (
                  <Stack direction='row' sx={transportSelectedItemStyle}>
                    <Paper elevation={0}>
                      <MapTransportConditionFilterFullList />
                    </Paper>
                    <Paper elevation={0}>
                      <Scrollbar>
                        <Stack direction='column' sx={scrollItemStyle}>
                          <Protect
                            permissions='read'
                            renderPermitted={() => (
                              <Paper variant='outlined'>
                                <Typography className='MuiPaper-label' variant='body1'>
                                  Отображение трека
                                </Typography>
                                <Stack className='MuiPaper-body'>
                                  <MapTransportConditionTrackList />
                                </Stack>
                              </Paper>
                            )}
                            renderForbidden={() => <React.Fragment />}
                          />
                          {galileosky_device_type && (
                            <Protect
                              permissions={['create', 'update', 'sensor']}
                              renderPermitted={() => (
                                <Paper variant='outlined'>
                                  <Typography className='MuiPaper-label' variant='body1'>
                                    Управление терминалом
                                  </Typography>
                                  <Stack className='MuiPaper-body'>
                                    <MapTransportConditionTerminalList />
                                  </Stack>
                                </Paper>
                              )}
                              renderForbidden={() => <React.Fragment />}
                            />
                          )}
                        </Stack>
                      </Scrollbar>
                    </Paper>
                  </Stack>
                )}
                <Stack direction='row' sx={transportConditionItemsStyle}>
                  <Paper elevation={0} tabIndex='-1'>
                    <MapTransportConditionList ref={transportConditionListRef} />
                  </Paper>
                  <Paper elevation={0} tabIndex='-1'>
                    {transportConditionSelectedItem && (
                      <Scrollbar>
                        <Stack direction='column' spacing={2} sx={transportConditionScrollItemStyle}>
                          <Stack direction='row' sx={gridColumnsStyle}>
                            <Paper variant='outlined'>
                              <Stack className='MuiPaper-label' direction='row' gap={1}>
                                <Typography variant='body1'>
                                  {moment(transportConditionSelectedItem.datetime).format('DD.MM.YYYY HH:mm:ss')},
                                </Typography>
                                <Typography variant='body1' color='text.secondary'>
                                  {moment(transportConditionSelectedItem.receive_date).format('DD.MM.YYYY HH:mm:ss')}
                                </Typography>
                                <Stack direction='row' sx={mapTransportConditionIconsListStyle}>
                                  <MapTransportConditionIconsList
                                    data={transportSelectedItem}
                                    datetime={datetimeInterval}
                                    history={
                                      transportConditionSelectedItem.datetime !==
                                        transportConditionItems[transportConditionItems.length - 1].datetime ||
                                      !moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day')
                                    }
                                    zoom={false}
                                    conditions={[transportConditionSelectedItem]}
                                  />
                                </Stack>
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Геозона</Typography>
                                <Typography
                                  variant='body1'
                                  sx={(theme) => ({
                                    'span:nth-of-type(2)': {
                                      color: theme.palette.text.secondary,
                                    },
                                  })}>
                                  {transportConditionSelectedItem.hdopData
                                    ? transportConditionSelectedItem.hdopData.geofence_id !== config.conditions.geofence_id.default
                                      ? transportConditionSelectedItem.hdopData.geofence_name
                                      : ''
                                    : transportConditionSelectedItem.data.geofence_id !== config.conditions.geofence_id.default
                                      ? transportConditionSelectedItem.data.geofence_name
                                      : ''}
                                </Typography>
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>События</Typography>
                                <React.Fragment>
                                  {transportConditionSelectedItem.messages.length !== 0 ? (
                                    <Typography variant='body1'>
                                      {transportConditionSelectedItem.messages
                                        .map((message) => {
                                          switch (message.name) {
                                            case 'geofence_id':
                                            case 'bt_id_1':
                                            case 'ibutton1':
                                              return `${message.body} «${message.value}»`;
                                            default:
                                              return message.body;
                                          }
                                        })
                                        .join(', ')}
                                    </Typography>
                                  ) : (
                                    <Typography variant='body1'>События для отображения трека</Typography>
                                  )}
                                </React.Fragment>
                              </Stack>
                            </Paper>
                            <Paper
                              variant='outlined'
                              sx={(theme) => ({
                                '.MuiPaper-row .MuiTypography-root:last-of-type:hover': {
                                  cursor: 'pointer',
                                  color: alpha(theme.palette.primary.main, 0.96),
                                },
                              })}>
                              <Typography className='MuiPaper-label' variant='body1'>
                                Госномер
                              </Typography>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>ТС</Typography>
                                <CopyToClipboard text={transportSelectedItem.number} />
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>ППЦ</Typography>
                                {transportConditionSelectedItem.data.trailer_number ? (
                                  <CopyToClipboard text={transportConditionSelectedItem.data.trailer_number} />
                                ) : (
                                  <Typography variant='body1'>N/a</Typography>
                                )}
                              </Stack>
                            </Paper>
                          </Stack>
                          <Stack direction='row' sx={voltageStyle}>
                            <Paper variant='outlined'>
                              <Typography className='MuiPaper-label' variant='body1'>
                                Питание
                              </Typography>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Борт</Typography>
                                <Typography
                                  variant='body1'
                                  sx={(theme) => ({
                                    ...(transportConditionSelectedItem.data.voltage !== config.conditions.voltage.default &&
                                      transportConditionSelectedItem.data.voltage < config.voltageLimit && {
                                        color: theme.palette.error.main,
                                      }),
                                  })}>
                                  {galileosky_device_type
                                    ? transportConditionSelectedItem.data.voltage !== config.conditions.voltage.default
                                      ? `${transportConditionSelectedItem.data.voltage.toLocaleString('ru-Ru')} ${config.conditions.voltage.unit}`
                                      : ''
                                    : ''}
                                </Typography>
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Аккумулятор</Typography>
                                <Typography variant='body1'>
                                  {galileosky_device_type
                                    ? transportConditionSelectedItem.data.acc_voltage !== config.conditions.acc_voltage.default
                                      ? `${transportConditionSelectedItem.data.acc_voltage.toLocaleString('ru-Ru')} ${config.conditions.acc_voltage.unit}`
                                      : ''
                                    : ''}
                                </Typography>
                              </Stack>
                            </Paper>
                            <Paper variant='outlined'>
                              <Typography className='MuiPaper-label' variant='body1'>
                                Температура
                              </Typography>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Внутри</Typography>
                                <Typography variant='body1'>
                                  {galileosky_device_type
                                    ? transportConditionSelectedItem.data.terminal_temperature !==
                                      config.conditions.terminal_temperature.default
                                      ? `${Math.round(transportConditionSelectedItem.data.terminal_temperature * 0.7)} ${config.conditions.terminal_temperature.unit}`
                                      : ''
                                    : ''}
                                </Typography>
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Снаружи</Typography>
                                <Typography variant='body1'>
                                  {galileosky_device_type
                                    ? transportConditionSelectedItem.data.env_temperature !== config.conditions.env_temperature.default
                                      ? `${transportConditionSelectedItem.data.env_temperature} ${config.conditions.env_temperature.unit}`
                                      : ''
                                    : ''}
                                </Typography>
                              </Stack>
                            </Paper>
                            <Paper
                              variant='outlined'
                              sx={{
                                '.MuiTypography-root:first-of-type': {
                                  minWidth: '72px!important',
                                },
                              }}>
                              <Typography className='MuiPaper-label' variant='body1'>
                                Сеть
                              </Typography>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Качество</Typography>
                                <Typography variant='body1' sx={signalStrengthStyle}>
                                  {config.numberOfSignalStrength.map((value, key) => (
                                    <Box
                                      key={key}
                                      component='span'
                                      sx={(theme) => ({
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                        ...(key < transportConditionSelectedItem.data.signal_strength && {
                                          bgcolor: alpha(theme.palette.primary.main, 0.96),
                                        }),
                                      })}></Box>
                                  ))}
                                </Typography>
                              </Stack>
                              <Stack className='MuiPaper-row'>
                                <Typography variant='body1'>Источник</Typography>
                                <Typography variant='body1'>
                                  {galileosky_device_type ? (
                                    <React.Fragment>
                                      {
                                        {
                                          NA: '',
                                          GPS: 'GPS',
                                          GSM: 'GSM',
                                          ERR: 'Ошибка',
                                        }[transportConditionSelectedItem.data.geodata]
                                      }{' '}
                                      {transportConditionSelectedItem.data.sats !== config.conditions.sats.default
                                        ? `(${transportConditionSelectedItem.data.sats}${transportConditionSelectedItem.data.hdop !== config.conditions.hdop.default ? ` / ${transportConditionSelectedItem.data.hdop}` : ''})`
                                        : ''}
                                    </React.Fragment>
                                  ) : (
                                    <React.Fragment />
                                  )}{' '}
                                </Typography>
                              </Stack>
                            </Paper>
                          </Stack>
                          {
                            <TanksBlock
                              transportSelectedItem={transportSelectedItem}
                              transportConditionSelectedItem={transportConditionSelectedItem}
                              transportConditionItems={transportConditionItems}
                              galileosky_device_type={galileosky_device_type}
                              guardPermission={guardPermission}
                              handleConditionGuardOutClick={handleConditionGuardOutClick}
                              sensorPermission={sensorPermission}
                              fuelLevelPermission={fuelLevelPermission}
                              declOfNum={declOfNum}
                              relayPermission={relayPermission}
                              transportSelectedDate={transportSelectedDate}
                            />
                          }
                          <Stack direction='row' sx={(theme) => canStyle(theme, transportConditionSelectedItem)}>
                            <Paper variant='outlined'>
                              <Typography className='MuiPaper-label' variant='body1'>
                                <span>CAN</span>
                                {galileosky_device_type && <TransportAccStatusIcon />}
                              </Typography>
                              <Stack className='MuiPaper-column'>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Скорость по GPS</Typography>
                                  <Typography variant='body1'>
                                    {['galileosky', 'onlyWialon'].includes(config.transportDeviceTypes[transportSelectedItem.device_type])
                                      ? transportConditionSelectedItem.data.speed !== config.conditions.speed.default
                                        ? transportConditionSelectedItem.data.speed > 0 &&
                                          `${Math.round(transportConditionSelectedItem.data.speed)} ${config.conditions.speed.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Скорость</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.can_speed !== config.conditions.can_speed.default
                                        ? transportConditionSelectedItem.data.can_speed > 0 &&
                                          `${Math.round(transportConditionSelectedItem.data.can_speed)} ${config.conditions.can_speed.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Обороты двигателя</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.engine_speed !== config.conditions.engine_speed.default
                                        ? transportConditionSelectedItem.data.engine_speed > 0 &&
                                          `${transportConditionSelectedItem.data.engine_speed} ${config.conditions.engine_speed.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Температура двигателя</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.engine_coolant_temperature !==
                                        config.conditions.engine_coolant_temperature.default
                                        ? transportConditionSelectedItem.data.engine_coolant_temperature > 0 &&
                                          `${transportConditionSelectedItem.data.engine_coolant_temperature} ${config.conditions.engine_coolant_temperature.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Педаль газа</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.gas_pedal !== config.conditions.gas_pedal.default
                                        ? transportConditionSelectedItem.data.gas_pedal > 0 &&
                                          `${transportConditionSelectedItem.data.gas_pedal} ${config.conditions.gas_pedal.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Нагрузка двигателя</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.engine_load !== config.conditions.engine_load.default
                                        ? transportConditionSelectedItem.data.engine_load > 0 &&
                                          `${transportConditionSelectedItem.data.engine_load} ${config.conditions.engine_load.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Уровень топлива</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.fuel_level !== config.conditions.fuel_level.default
                                        ? transportConditionSelectedItem.data.fuel_level > 0 &&
                                          `${Math.round((transportSelectedItem.volume * transportConditionSelectedItem.data.fuel_level) / 100)} ${config.conditions.fuel_level.unit} (${Math.round(transportConditionSelectedItem.data.fuel_level)} %)`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                              </Stack>
                              <Stack className='MuiPaper-column'>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Пробег</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.total_mileage !== config.conditions.total_mileage.default
                                        ? transportConditionSelectedItem.data.total_mileage > 0 &&
                                          `${Math.round(transportConditionSelectedItem.data.total_mileage / 1000).toLocaleString('ru-RU')} ${config.conditions.total_mileage.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Расход топлива</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? transportConditionSelectedItem.data.total_fuel_consumption !==
                                        config.conditions.total_fuel_consumption.default
                                        ? transportConditionSelectedItem.data.total_fuel_consumption > 0 &&
                                          `${(transportConditionSelectedItem.data.total_fuel_consumption / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ${config.conditions.total_fuel_consumption.unit}`
                                        : ''
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Ручной тормоз</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? {
                                          ON: 'Включен',
                                          OFF: 'Выключен',
                                          ERR: 'Ошибка',
                                          NA: '',
                                        }[transportConditionSelectedItem.data.parking_brake]
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Ремень безопасности</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? {
                                          ON: 'Пристегнут',
                                          OFF: 'Не пристегнут',
                                          ERR: 'Ошибка',
                                          NA: '',
                                        }[transportConditionSelectedItem.data.seatbelt]
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Круиз контроль</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? {
                                          ON: 'Включен',
                                          OFF: 'Выключен',
                                          ERR: 'Ошибка',
                                          NA: '',
                                        }[transportConditionSelectedItem.data.cruise_control]
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Нагрузка на оси</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type &&
                                      (transportConditionSelectedItem.data.axle1_load !== config.conditions.axle1_load.default ||
                                        transportConditionSelectedItem.data.axle2_load !== config.conditions.axle2_load.default) && (
                                        <React.Fragment>
                                          <React.Fragment>
                                            {transportConditionSelectedItem.data.axle1_load !== config.conditions.axle1_load.default
                                              ? `${(transportConditionSelectedItem.data.axle1_load * 0.001).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} /`
                                              : ' /'}
                                          </React.Fragment>
                                          <React.Fragment>
                                            {transportConditionSelectedItem.data.axle2_load !== config.conditions.axle2_load.default
                                              ? ` ${(transportConditionSelectedItem.data.axle2_load * 0.001).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`
                                              : ''}
                                          </React.Fragment>
                                          <React.Fragment>{` ${config.conditions.axle2_load.unit}`}</React.Fragment>
                                        </React.Fragment>
                                      )}
                                  </Typography>
                                </Stack>
                                <Stack className='MuiPaper-row'>
                                  <Typography variant='body1'>Антиблокировочная система</Typography>
                                  <Typography variant='body1'>
                                    {galileosky_device_type
                                      ? {
                                          ON: 'Включена',
                                          OFF: 'Выключена',
                                          ERR: 'Ошибка',
                                          NA: '',
                                        }[transportConditionSelectedItem.data.abs_control]
                                      : ''}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Paper>
                          </Stack>
                        </Stack>
                      </Scrollbar>
                    )}
                    {!transportConditionSelectedItem && (
                      <Stack
                        direction='column'
                        alignItems='center'
                        justifyContent='center'
                        sx={(theme) => ({
                          width: '100%',
                          height: '100%',
                          padding: theme.spacing(2),
                        })}>
                        <Typography align='center' variant='body1' component='span'>
                          Ничего не найдено
                        </Typography>
                        <Typography align='center' color='text.secondary' variant='caption' component='span'>
                          Ни одно событие не соответствует выбранным фильтрам
                        </Typography>
                      </Stack>
                    )}
                  </Paper>
                </Stack>
                <Stack direction='row' alignItems='center' sx={lastRowStyle}>
                  <Stack
                    direction='row'
                    sx={{
                      marginLeft: '12px',
                      whiteSpace: 'nowrap',
                      'span:nth-of-type(2)': {
                        paddingLeft: '4px',
                      },
                    }}>
                    <Typography component='span'>Нет сообщений</Typography>
                    <Typography component='span'>
                      {moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') ? (
                        <MapTransportConditionTimer lastTime={lastMessageTime} />
                      ) : (
                        <Typography component='span'>{moment().startOf('day').format('HH:mm:ss')}</Typography>
                      )}
                    </Typography>
                  </Stack>
                  <MapTransportConditionFilterList />
                  <Stack direction='row' sx={(theme) => conditionOptionsStyle(theme, transportSelectedItem)}>
                    <Divider orientation='vertical' variant='middle' flexItem />
                    <Tooltip title='Настройки отображения' placement='top' arrow enterDelay={800}>
                      <IconButton disableRipple disableFocusRipple onClick={handleConditionOptionsOpenClick}>
                        <Settings fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {!isFetchConditionLoading && transportConditionItems.length === 0 && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={(theme) => ({
                  width: '100%',
                  height: '100%',
                  padding: theme.spacing(2),
                })}>
                <Typography align='center' variant='body1' component='span'>
                  Здесь пока ничего нет
                </Typography>
                <Typography align='center' color='text.secondary' variant='caption' component='span'>
                  В этом разделе будет находиться список состояний транспорта
                </Typography>
              </Stack>
            )}
            {isFetchConditionLoading && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={(theme) => ({
                  width: '100%',
                  height: '100%',
                  padding: theme.spacing(2),
                })}>
                <CircularProgress size={24} />
              </Stack>
            )}
          </React.Fragment>
        )}
        renderForbidden={RenderForbidden}
      />
    </TabPanel>
  );
};
export default TabPanelCondition;
